import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import API from '../utils/api';
import { toast } from 'react-toastify';
import OtpInput from '../components/OtpInput';
import BrandLogo from '../components/BrandLogo';

export default function ForgotPassword() {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const requestOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await API.post('/auth/forgot-password', { email });
      toast.success('Reset OTP sent to your email');
      setStep('reset');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to send reset OTP');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (otp.length !== 6) return setError('Enter the 6 digit OTP');
    if (password.length < 6) return setError('Password must be at least 6 characters');
    if (password !== confirmPw) return setError('Passwords do not match');
    setLoading(true);
    try {
      await API.post('/auth/reset-password', { email, otp, password });
      toast.success('Password reset successfully. Please login.');
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page auth-login-page" style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#1A1A1A 0%,#2D2D2D 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="auth-back" style={{ width: '100%', maxWidth: 440, marginBottom: 12 }}>
        <button onClick={() => navigate('/login')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, padding: '7px 14px', color: '#9CA3AF', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
          <ArrowLeft size={15} /> Back to Login
        </button>
      </div>

      <div style={{ background: 'white', borderRadius: 20, padding: '32px 28px', width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }} className="fade-in auth-card auth-login-card">
        <div className="auth-header" style={{ textAlign: 'center', marginBottom: 22 }}>
          <BrandLogo size={56} radius={16} fallbackIcon={ShieldCheck} />
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{step === 'email' ? 'Forgot Password' : 'Reset Password'}</h1>
          <p style={{ color: 'var(--gray)', fontSize: 13 }}>{step === 'email' ? 'Enter your email to receive an OTP' : 'Enter OTP and create a new password'}</p>
        </div>

        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: 'var(--red)', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{error}</div>
        )}

        {step === 'email' ? (
          <form onSubmit={requestOtp}>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <div style={{ position: 'relative' }}>
                <Mail size={15} color="var(--gray)" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input className="form-input" style={{ paddingLeft: 40 }} type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} required />
              </div>
            </div>
            <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '12px' }} disabled={loading}>
              {loading ? 'Sending OTP...' : 'Send Reset OTP'}
            </button>
          </form>
        ) : (
          <form onSubmit={resetPassword}>
            <div className="form-group">
              <label className="form-label">OTP</label>
              <OtpInput value={otp} onChange={setOtp} disabled={loading} />
            </div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <div style={{ position: 'relative' }}>
                <Lock size={15} color="var(--gray)" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input className="form-input" style={{ paddingLeft: 40, paddingRight: 42 }} type={showPw ? 'text' : 'password'} placeholder="New password" value={password} onChange={e => setPassword(e.target.value)} required />
                <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', display: 'flex' }}>
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input className="form-input" type="password" placeholder="Repeat password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required />
            </div>
            <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '12px' }} disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <button type="button" onClick={requestOtp} disabled={loading} style={{ background: 'none', color: 'var(--orange)', fontWeight: 700, fontSize: 13, marginTop: 12 }}>
              Resend OTP
            </button>
          </form>
        )}

        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--gray)', marginTop: 16 }}>
          Remember password? <Link to="/login" style={{ color: 'var(--orange)', fontWeight: 700 }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}
